import React, { useState, useEffect } from "react";
import { marked } from "marked";
import "./ShowDetails.scss";
import ContentCard from "../content-card/ContentCard";
import IconSendMail from "../../common/icons/IconSendMail";
import IconCopy from "../../common/icons/IconCopy";
import IconClose from "../../common/icons/IconClose";
import { toast } from "react-toastify";
import { AI_BASE_URL, API_BASE_URL } from "../../config/index";

// Load environment variables

// Models for comparison dropdown
const models = [
  "qwen-plus",
  "qwen-max",
  "gpt-4o-mini",
  "gemini-1.5-flash",
  "gemini-1.5-pro",
  "gpt-4o",
  "gpt-4.1",
  "gpt-5",
  "gpt-5-mini",
  "gpt-5-nano",
];

const ShowDetails = ({ data, token, user }) => {
  const [sourcesData, setSourcesData] = useState([]);
  const [answerData, setAnswerData] = useState(null);

  const [displayMailPanel, setDisplayMailPanel] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [subject, setSubject] = useState(
    `Vous m'avez posé la question suivante :\n\n${data.body?.input}`
  );
  const [message, setMessage] = useState("");

  const [selectedModel, setSelectedModel] = useState(models[0]);
  const [comparisonAnswer, setComparisonAnswer] = useState(null);
  const [isComparing, setIsComparing] = useState(false);

  useEffect(() => {
    setAnswerData(data.body);
    setSubject(
      `Vous m'avez posé la question suivante :\n\n${data.body?.input}`
    );
  }, [data]);

  const getFormattedAnswer = (answer) => {
    return answer
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  };

  const toggleMailPanel = () => {
    setDisplayMailPanel(!displayMailPanel);
    setMessage(getFormattedAnswer(marked(data.body?.answer)));
  };

  const copyFormattedAnswer = () => {
    try {
      const formattedAnswer = getFormattedAnswer(
        marked(answerData?.answer || "")
      );
      navigator.clipboard.writeText(formattedAnswer);
      toast.success("Réponse copiée !", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error("Erreur lors de la copie", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const removeDuplicates = (arr, key) => {
    const seen = new Set();
    return arr.filter((obj) => {
      if (obj[key] && seen.has(obj[key])) {
        return false;
      } else if (obj[key]) {
        seen.add(obj[key]);
        return true;
      }
      return true;
    });
  };

  useEffect(() => {
    if (!answerData) return;

    let sources = removeDuplicates(answerData.sources, "rubric");
    sources = removeDuplicates(sources, "source");
    sources = removeDuplicates(sources, "id");

    const fetchSources = async () => {
      const fetchSourceData = async (source) => {
        let urlSrc = "";
        if (source?.type === "RUBRIC") {
          urlSrc = `${API_BASE_URL}/media/faq-rubric?access_token=${token}&filter=[%7B%22property%22:%22id%22,%22value%22:${Math.floor(
            source.rubric
          )},%22operator%22:%22eq%22%7D]&fields=*,children,chain,author`;
        } else if (source?.type === "ARTICLE") {
          urlSrc = `${API_BASE_URL}/blog/article?access_token=${token}&filter=[%7B%22property%22:%22id%22,%22value%22:${Math.floor(
            source.id
          )},%22operator%22:%22eq%22%7D]&fields=*,author,organization,chains`;
        }

        if (source?.type === "PDF") {
          const sourceData = { ...source, type: source?.type };
          return sourceData;
        } else if (source?.type === "VIDEO") {
          return {
            type: source?.type,
            description: source?.description,
            video_url: source?.video_url,
            video_thumbnail: source?.video_thumbnail,
            start_time: source?.start_time,
            end_time: source?.end_time,
          };
        } else {
          try {
            const response = await fetch(urlSrc, { method: "GET" });
            if (!response.ok) {
              throw new Error(
                `Failed to fetch source data for id ${source.id}`
              );
            }
            let sourceData = await response.json();
            sourceData = { ...sourceData, type: source?.type };
            return sourceData;
          } catch (error) {
            console.error("Error fetching source data:", error);
            return null;
          }
        }
      };

      const sourceDataPromises = sources.map((source) =>
        fetchSourceData(source)
      );
      const results = await Promise.all(sourceDataPromises);
      setSourcesData(results.filter((source) => source !== null));
      setSubject(
        `Vous m'avez posé la question suivante :\n\n${data.body?.input}`
      );
    };

    fetchSources();
  }, [answerData, token]);

  const fetchComparisonAnswer = async () => {
    if (!data.body?.input) return;
    setIsComparing(true);
    try {
      const response = await fetch(`${AI_BASE_URL}/get_answer_stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: data.body.input,
          model: selectedModel,
          token: token,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let collectedText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const parsedData = JSON.parse(line.slice(6));
              switch (parsedData?.type) {
                case "token":
                  collectedText += parsedData.data;
                  break;
                case "error":
                  console.error("Error from server:", parsedData.data);
                  throw new Error(parsedData.data);
                default:
                  break;
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e);
            }
          }
        }
      }
      setComparisonAnswer(collectedText);
    } catch (error) {
      console.error("Error fetching comparison answer:", error);
      toast.error("Erreur lors de la comparaison", {
        position: "top-right",
        autoClose: 2000,
      });
    } finally {
      setIsComparing(false);
    }
  };

  return (
    <div className="show-details-split">
      {/* Left Pane: Original content displaying question, answer and sources */}
      <div className="left-pane">
        <h2>Question :</h2>
        <p className="show-question">{data.body.input}</p>
        <h2>Réponse :</h2>
        <p
          className="show-answer"
          dangerouslySetInnerHTML={{
            __html: marked(answerData?.answer || ""),
          }}
        ></p>
        <div className="action-buttons">
          <button className="action-button" onClick={toggleMailPanel}>
            <IconSendMail />
          </button>
          <button className="action-button" onClick={copyFormattedAnswer}>
            <IconCopy />
          </button>
        </div>
        <div className="border-bottom"></div>
        {sourcesData.length > 0 && <h2>Sources :</h2>}
        <div className="cards-container">
          {sourcesData?.map((sourceData, index) => (
            <React.Fragment key={index}>
              {sourceData && sourceData?.type && (
                <div className="cards">
                  {sourceData?.type === "RUBRIC" ? (
                    <ContentCard
                      type={sourceData?.type}
                      question={sourceData.data[0].titleFr}
                      publishDate={
                        sourceData.data[0].questions[0].updatedAt
                          ? sourceData.data[0].questions[0].updatedAt.date
                          : sourceData.data[0].questions[0].createdAt.date
                      }
                      chain={sourceData.data[0].chain}
                      author={sourceData.data[0].author}
                      rubric={sourceData.data[0]}
                    />
                  ) : sourceData?.type === "ARTICLE" ? (
                    answerData.answered && (
                      <ContentCard
                        type={sourceData?.type}
                        question={sourceData.data[0].title}
                        publishDate={
                          sourceData.data[0].updatedAt
                            ? sourceData.data[0].updatedAt
                            : sourceData.data[0].publishedAt
                        }
                        chain={sourceData.data[0].chains}
                        author={sourceData.data[0].author}
                        id={sourceData.data[0].id}
                      />
                    )
                  ) : sourceData?.type === "VIDEO" ? (
                    answerData.answered && (
                      <ContentCard
                        type={sourceData.data?.type || sourceData?.type}
                        question={
                          sourceData.data?.description ||
                          sourceData?.description
                        }
                        url={
                          sourceData.data?.video_url || sourceData?.video_url
                        }
                        coverUrl={
                          sourceData.data?.video_thumbnail ||
                          sourceData?.video_thumbnail ||
                          "fallback-image-url"
                        }
                        startTime={
                          sourceData.data?.start_time || sourceData?.start_time
                        }
                        endTime={
                          sourceData.data?.end_time || sourceData?.end_time
                        }
                      />
                    )
                  ) : (
                    answerData.answered &&
                    sourceData.royalty === "free" && (
                      <ContentCard
                        type={sourceData?.type}
                        question={sourceData.source}
                        page={sourceData.page}
                        url={sourceData.url || sourceData.link}
                        coverUrl={sourceData.cover_url}
                        fileObjectKey={
                          sourceData.objectKey || sourceData.object_key
                        }
                      />
                    )
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Right Pane: Comparison feature */}
      {user.id == 8650 && (
        <div className="right-pane">
          <h2>Comparer les modèles</h2>
          <div className="model-selector">
            <label htmlFor="model-select">Sélectionnez le modèle:</label>
            <select
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {models.map((model, index) => (
                <option key={index} value={model}>
                  {model}
                </option>
              ))}
            </select>
            <button
              className="compare-button"
              onClick={fetchComparisonAnswer}
              disabled={isComparing}
            >
              {isComparing ? "Comparaison en cours..." : "Comparer"}
            </button>
          </div>
          {comparisonAnswer && (
            <div className="comparison-results">
              <div className="result-column">
                <h3>Réponse ({selectedModel})</h3>
                <div
                  dangerouslySetInnerHTML={{
                    __html: marked(comparisonAnswer),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {displayMailPanel && (
        <div className="mail-panel-overlay" onClick={toggleMailPanel}>
          <div className="mail-panel open" onClick={(e) => e.stopPropagation()}>
            <div className="mail-panel-header">
              <div className="mail-panel-title">
                <h3>Envoyer par mail</h3>
                <div className="mail-input">
                  <label className="mail-label" htmlFor="mail">
                    Destinataire :
                  </label>
                  <input
                    type="email"
                    id="mail"
                    name="mail"
                    onChange={(e) => setRecipient(e.target.value)}
                  />
                </div>
              </div>
              <div className="mail-panel-close" onClick={toggleMailPanel}>
                <IconClose />
              </div>
            </div>
            <div className="mail-panel-content">
              <div className="mail-panel-cards">
                <div className="mail-input">
                  <label className="mail-label" htmlFor="object">
                    Objet :
                  </label>
                  <textarea
                    name="object"
                    rows={3}
                    id="object"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
                <div className="mail-input">
                  <label className="mail-label" htmlFor="message">
                    Message :
                  </label>
                  <textarea
                    name="message"
                    rows={14}
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
              </div>
              <div className="mail-panel-buttons">
                <a
                  href={`mailto:${recipient}?subject=${encodeURIComponent(
                    subject
                  )}&body=${encodeURIComponent(message)}`}
                  onClick={(e) => {
                    if (!recipient) {
                      e.preventDefault();
                      alert("Veuillez entrer une adresse e-mail.");
                    }
                  }}
                >
                  ENVOYER LE MESSAGE
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowDetails;
